import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Clock,
  Plus,
  Play,
  Pencil,
  Trash2,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import Badge from '@/components/ui/Badge';
import Card from '@/components/ui/Card';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import Button from '@/components/ui/Button';
import ScheduleFormModal from '@/components/admin/ScheduleFormModal';
import api from '@/lib/api';
import { useUIStore } from '@/stores/ui-store';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AgentSchedule {
  id: string;
  name: string;
  description: string | null;
  task_type: string;
  cron_expression: string;
  cron_human: string | null;
  is_active: boolean;
  created_by: string;
  last_run_at: string | null;
  last_run_status: string | null;
  next_run_at: string | null;
  run_count: number;
  context: Record<string, unknown> | null;
  created_at: string;
}

interface ScheduleStats {
  active_schedules: number;
  runs_today: number;
  success_rate: number;
  cost_today: number;
}

interface SchedulesResponse {
  schedules: AgentSchedule[];
  total: number;
  page: number;
  limit: number;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const TASK_TYPE_OPTIONS = [
  '',
  'supplier_discovery',
  'catalog_curation',
  'pricing_review',
  'order_fulfillment',
  'catalog_sync',
  'supplier_research',
  'product_listing',
];

const taskTypeBadge: Record<string, { variant: 'blue' | 'green' | 'yellow' | 'red' | 'gray'; label: string }> = {
  supplier_discovery: { variant: 'blue', label: 'Supplier Discovery' },
  catalog_curation: { variant: 'green', label: 'Catalog Curation' },
  pricing_review: { variant: 'yellow', label: 'Pricing Review' },
  order_fulfillment: { variant: 'red', label: 'Order Fulfillment' },
  catalog_sync: { variant: 'gray', label: 'Catalog Sync' },
  supplier_research: { variant: 'blue', label: 'Supplier Research' },
  product_listing: { variant: 'green', label: 'Product Listing' },
};

const runStatusBadge: Record<string, { variant: 'green' | 'red' | 'blue' | 'yellow' | 'gray'; label: string }> = {
  success: { variant: 'green', label: 'Success' },
  completed: { variant: 'green', label: 'Success' },
  failed: { variant: 'red', label: 'Failed' },
  running: { variant: 'blue', label: 'Running' },
  escalated: { variant: 'yellow', label: 'Escalated' },
};

function relativeTime(dateStr: string | null): string {
  if (!dateStr) return '—';
  const diff = Date.now() - new Date(dateStr).getTime();
  const abs = Math.abs(diff);
  const future = diff < 0;
  const prefix = future ? 'in ' : '';
  const suffix = future ? '' : ' ago';

  if (abs < 60_000) return 'just now';
  if (abs < 3600_000) {
    const m = Math.round(abs / 60_000);
    return `${prefix}${m}m${suffix}`;
  }
  if (abs < 86400_000) {
    const h = Math.round(abs / 3600_000);
    return `${prefix}${h}h${suffix}`;
  }
  const d = Math.round(abs / 86400_000);
  return `${prefix}${d}d${suffix}`;
}

// ---------------------------------------------------------------------------
// Stats cards
// ---------------------------------------------------------------------------

function StatsCards({ stats }: { stats: ScheduleStats | null }) {
  if (!stats) return null;

  const cards = [
    { label: 'Active Schedules', value: stats.active_schedules.toString() },
    { label: 'Runs Today', value: stats.runs_today.toString() },
    { label: 'Success Rate', value: `${Math.round(stats.success_rate ?? 0)}%` },
    { label: 'Est. Cost Today', value: `$${(stats.cost_today ?? 0).toFixed(4)}` },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {cards.map((c) => (
        <Card key={c.label} padding="md">
          <p className="text-sm text-gray-500">{c.label}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{c.value}</p>
        </Card>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Toggle switch
// ---------------------------------------------------------------------------

function ToggleSwitch({ checked, onChange, disabled }: { checked: boolean; onChange: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={onChange}
      disabled={disabled}
      className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed ${
        checked ? 'bg-green-500' : 'bg-gray-300'
      }`}
    >
      <span
        className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition-transform ${
          checked ? 'translate-x-4' : 'translate-x-0'
        }`}
      />
    </button>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function AgentSchedules() {
  const addToast = useUIStore((s) => s.addToast);

  const [schedules, setSchedules] = useState<AgentSchedule[]>([]);
  const [stats, setStats] = useState<ScheduleStats | null>(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, total_pages: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [runningId, setRunningId] = useState<string | null>(null);

  // Filters
  const [taskTypeFilter, setTaskTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [createdByFilter, setCreatedByFilter] = useState('');

  // Modal
  const [showModal, setShowModal] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState<AgentSchedule | null>(null);

  const fetchStats = async () => {
    try {
      const res = await api.get<ScheduleStats>('/admin/agent/schedules/stats');
      setStats(res);
    } catch {
      // non-critical
    }
  };

  const fetchSchedules = async (page: number) => {
    try {
      setIsLoading(true);
      const params = new URLSearchParams();
      params.set('page', page.toString());
      params.set('limit', '20');
      if (taskTypeFilter) params.set('task_type', taskTypeFilter);
      if (statusFilter === 'active') params.set('is_active', 'true');
      if (statusFilter === 'inactive') params.set('is_active', 'false');
      if (createdByFilter) params.set('created_by', createdByFilter);

      const res = await api.get<SchedulesResponse>(`/admin/agent/schedules?${params.toString()}`);
      setSchedules(res.schedules || []);
      const total = res.total ?? 0;
      const limit = res.limit ?? 20;
      setPagination({ page: res.page ?? 1, limit, total, total_pages: Math.ceil(total / limit) || 1 });
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Failed to load schedules', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSchedules(1);
    fetchStats();
  }, [taskTypeFilter, statusFilter, createdByFilter]);

  useEffect(() => {
    fetchSchedules(pagination.page);
  }, [pagination.page]);

  const handleToggle = async (schedule: AgentSchedule) => {
    setTogglingId(schedule.id);
    try {
      const updated = await api.post<AgentSchedule>(`/admin/agent/schedules/${schedule.id}/toggle`, {});
      setSchedules((prev) => prev.map((s) => (s.id === schedule.id ? { ...s, is_active: updated.is_active } : s)));
      fetchStats();
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Failed to toggle schedule', 'error');
    } finally {
      setTogglingId(null);
    }
  };

  const handleRunNow = async (schedule: AgentSchedule) => {
    setRunningId(schedule.id);
    try {
      await api.post(`/admin/agent/schedules/${schedule.id}/run-now`, {});
      addToast(`Schedule "${schedule.name}" triggered`, 'success');
      fetchStats();
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Failed to run schedule', 'error');
    } finally {
      setRunningId(null);
    }
  };

  const handleDelete = async (schedule: AgentSchedule) => {
    if (!confirm(`Delete schedule "${schedule.name}"? This will not affect tasks already created from it.`)) return;
    try {
      await api.delete(`/admin/agent/schedules/${schedule.id}`);
      setSchedules((prev) => prev.filter((s) => s.id !== schedule.id));
      fetchStats();
      addToast('Schedule deleted', 'success');
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Failed to delete schedule', 'error');
    }
  };

  const handleEdit = (schedule: AgentSchedule) => {
    setEditingSchedule(schedule);
    setShowModal(true);
  };

  const handleCreate = () => {
    setEditingSchedule(null);
    setShowModal(true);
  };

  const handleSaved = (saved: AgentSchedule) => {
    if (editingSchedule) {
      setSchedules((prev) => prev.map((s) => (s.id === saved.id ? saved : s)));
    } else {
      setSchedules((prev) => [saved, ...prev]);
    }
    fetchStats();
  };

  const taskType = (s: AgentSchedule) => taskTypeBadge[s.task_type] ?? { variant: 'gray' as const, label: s.task_type };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Schedules</h1>
          <p className="text-gray-600 mt-1">Automate agent tasks on a recurring schedule.</p>
        </div>
        <Button onClick={handleCreate}>
          <Plus className="w-4 h-4 mr-2" />
          Create Schedule
        </Button>
      </div>

      {/* Stats */}
      <StatsCards stats={stats} />

      {/* Filters */}
      <Card padding="md">
        <div className="flex flex-wrap gap-4">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">Task Type</label>
            <select
              value={taskTypeFilter}
              onChange={(e) => setTaskTypeFilter(e.target.value)}
              className="text-sm border border-gray-300 rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              {TASK_TYPE_OPTIONS.map((t) => (
                <option key={t} value={t}>
                  {t === '' ? 'All types' : t.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="text-sm border border-gray-300 rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="active">Active</option>
              <option value="all">All</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">Created By</label>
            <select
              value={createdByFilter}
              onChange={(e) => setCreatedByFilter(e.target.value)}
              className="text-sm border border-gray-300 rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="">All</option>
              <option value="admin">Admin</option>
              <option value="agent">Agent</option>
            </select>
          </div>
        </div>
      </Card>

      {/* Table */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : schedules.length === 0 ? (
        <Card padding="lg">
          <div className="text-center py-12">
            <Clock className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No schedules found</h3>
            <p className="text-gray-500 mb-4">Create a schedule to automate agent tasks.</p>
            <Button onClick={handleCreate}>
              <Plus className="w-4 h-4 mr-2" />
              Create Schedule
            </Button>
          </div>
        </Card>
      ) : (
        <Card padding="none">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left">
                  <th className="px-4 py-3 font-medium text-gray-500">Name</th>
                  <th className="px-4 py-3 font-medium text-gray-500">Task Type</th>
                  <th className="px-4 py-3 font-medium text-gray-500">Schedule</th>
                  <th className="px-4 py-3 font-medium text-gray-500">Active</th>
                  <th className="px-4 py-3 font-medium text-gray-500">Created By</th>
                  <th className="px-4 py-3 font-medium text-gray-500">Last Run</th>
                  <th className="px-4 py-3 font-medium text-gray-500">Next Run</th>
                  <th className="px-4 py-3 font-medium text-gray-500 text-right">Runs</th>
                  <th className="px-4 py-3 font-medium text-gray-500 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {schedules.map((schedule) => {
                  const tt = taskType(schedule);
                  const lastRunBadge = schedule.last_run_status
                    ? (runStatusBadge[schedule.last_run_status] ?? { variant: 'gray' as const, label: schedule.last_run_status })
                    : null;

                  return (
                    <tr key={schedule.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">
                        <Link
                          to={`/admin/agent/schedules/${schedule.id}`}
                          className="font-medium text-gray-900 hover:text-green-700 hover:underline"
                        >
                          {schedule.name}
                        </Link>
                        {schedule.description && (
                          <p className="text-xs text-gray-400 truncate max-w-[200px]">{schedule.description}</p>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant={tt.variant}>{tt.label}</Badge>
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        {schedule.cron_human ?? schedule.cron_expression}
                      </td>
                      <td className="px-4 py-3">
                        <ToggleSwitch
                          checked={schedule.is_active}
                          onChange={() => handleToggle(schedule)}
                          disabled={togglingId === schedule.id}
                        />
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant={schedule.created_by === 'agent' ? 'yellow' : 'blue'}>
                          {schedule.created_by === 'agent' ? 'Agent' : 'Admin'}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="text-gray-500">{relativeTime(schedule.last_run_at)}</span>
                          {lastRunBadge && (
                            <Badge variant={lastRunBadge.variant}>{lastRunBadge.label}</Badge>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-500">
                        {relativeTime(schedule.next_run_at)}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-700 font-medium">
                        {schedule.run_count}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1 justify-end">
                          <button
                            onClick={() => handleRunNow(schedule)}
                            disabled={runningId === schedule.id}
                            title="Run now"
                            className="p-1.5 rounded text-gray-400 hover:text-green-600 hover:bg-green-50 transition-colors disabled:opacity-50"
                          >
                            <Play className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleEdit(schedule)}
                            title="Edit"
                            className="p-1.5 rounded text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(schedule)}
                            title="Delete"
                            className="p-1.5 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.total_pages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
              <p className="text-sm text-gray-500">
                {pagination.total} schedule{pagination.total !== 1 ? 's' : ''}
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPagination((p) => ({ ...p, page: p.page - 1 }))}
                  disabled={pagination.page <= 1}
                  className="p-1.5 rounded text-gray-400 hover:text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-sm text-gray-600">
                  {pagination.page} / {pagination.total_pages}
                </span>
                <button
                  onClick={() => setPagination((p) => ({ ...p, page: p.page + 1 }))}
                  disabled={pagination.page >= pagination.total_pages}
                  className="p-1.5 rounded text-gray-400 hover:text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Create/Edit Modal */}
      <ScheduleFormModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onSaved={handleSaved}
        editingSchedule={editingSchedule}
      />
    </div>
  );
}
